import React, { useState, useContext } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    Typography,
    IconButton,
    InputAdornment,
    Alert,
    CircularProgress,
    Slide,
} from '@mui/material';

import {
    Close,
    Visibility,
    VisibilityOff,
    Email,
    Lock,
    AdminPanelSettings,
} from '@mui/icons-material';

// import { loginUser } from '../../api/auth';
import AuthContext from '../../context/AuthContext';
import { PRIMARY_BTN_SX, FILLED_INPUT_SX } from '../../styles/buttons';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="left" ref={ref} {...props} />;
});

const AdminLoginPopup = ({ open, onClose }) => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loginError, setLoginError] = useState('');
    const athCtx = useContext(AuthContext);
    const navigate = useNavigate();

    const validateForm = () => {
        const newErrors = {};

        // Email validation
        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        // Password validation
        if (!formData.password) {
            newErrors.password = 'Password is required';
        } else if (formData.password.length < 6) {
            newErrors.password = 'Password must be at least 6 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }

        // Clear login error
        if (loginError) {
            setLoginError('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }
        setIsLoading(true);
        const requestData = { ...formData, role: 'admin' }

        const getData = athCtx.login(requestData.email, requestData.password, requestData.role);
        getData.then((res) => {
            if(res.success){
                navigate('/admin/dashboard');
                return;
            }
            if(!res.success){
                setLoginError(res.error || 'Login failed. Please try again.');
            }
        }).catch((error) => {
            console.log(error)
            
        }).finally(() => {
            setIsLoading(false);
        })
    };

    const handleClose = () => {
        setFormData({ email: '', password: '' });
        setErrors({});
        setLoginError('');
        setShowPassword(false);
        onClose();
    };

    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    position: 'fixed',
                    right: 0,
                    top: 0,
                    height: '100vh',
                    maxHeight: '100vh',
                    margin: 0,
                    borderRadius: '0',
                    width: { xs: '100%', sm: '450px' },
                    maxWidth: { xs: '100%', sm: '450px' },
                }
            }}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                }
            }}
        >
            <DialogContent className="p-0 h-full overflow-hidden admin-dialog">
                <div className="h-full flex flex-col">
                    {/* Header */}
                    <div className="bg-gradient-to-br from-talexo-blue to-talexo-purple text-white p-6 relative">
                        {/* Positioned via sx rather than Tailwind classes: MUI's own
                            IconButton styles were winning over `absolute right-4`, so
                            the close button rendered at the top-left, overlapping the
                            title. sx sits inside MUI's cascade and applies reliably.
                            Styled to match the dialog close button on Jobs by
                            Designation. */}
                        <IconButton
                            onClick={handleClose}
                            aria-label="Close"
                            size="small"
                            sx={{
                                position: 'absolute',
                                top: 16,
                                right: 16,
                                color: '#fff',
                                backgroundColor: 'rgba(255,255,255,0.15)',
                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)' },
                            }}
                        >
                            <Close fontSize="small" />
                        </IconButton>

                        {/* pr-12 keeps the heading clear of the close button */}
                        <div className="flex items-center gap-3 mb-2 pr-12">
                            <AdminPanelSettings className="text-3xl" />
                            <Typography variant="h5" className="font-bold">
                                Admin Login
                            </Typography>
                        </div>
                        <Typography variant="body2" className="opacity-90">
                            Access your admin dashboard
                        </Typography>
                    </div>

                    {/* Form Content */}
                    <div className="flex-1 p-6 overflow-y-auto">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            {loginError && (
                                <Alert severity="error" className="mb-4">
                                    {loginError}
                                </Alert>
                            )}

                            {/* Email Field */}
                            <div>
                                <Typography variant="body2" className="font-medium text-gray-700 mb-2">
                                    Email Address *
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="email"
                                    value={formData.email}
                                    onChange={handleInputChange('email')}
                                    error={!!errors.email}
                                    helperText={errors.email}
                                    placeholder="Enter your email address"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Email className="text-gray-400" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={FILLED_INPUT_SX}
                                />
                            </div>

                            {/* Password Field */}
                            <div>
                                <Typography variant="body2" className="font-medium text-gray-700 mb-2">
                                    Password *
                                </Typography>
                                <TextField
                                    fullWidth
                                    type={showPassword ? 'text' : 'password'}
                                    value={formData.password}
                                    onChange={handleInputChange('password')}
                                    error={!!errors.password}
                                    helperText={errors.password}
                                    placeholder="Enter your password"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock className="text-gray-400" />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                    size="small"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={FILLED_INPUT_SX}
                                />
                            </div>

                            {/* Demo Credentials Info */}

                            {/* Login Button */}
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={isLoading}
                                sx={{ ...PRIMARY_BTN_SX, borderRadius: '8px', height: '48px' }}
                            >
                                {isLoading ? (
                                    <div className="flex items-center gap-2">
                                        <CircularProgress size={20} color="inherit" />
                                        <span>Signing In...</span>
                                    </div>
                                ) : (
                                    'Sign In to Admin Panel'
                                )}
                            </Button>

                            {/* Forgot Password Link */}
                            <div className="text-center">
                                 <Link
                                    to="/forgot-password"
                                    onClick={handleClose}
                                    className="text-talexo-blue hover:text-talexo-blue-dark font-medium"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        </form>
                    </div>

                    {/* Footer */}
                    <div className="border-t border-gray-200 p-6 bg-gray-50">
                        <Typography variant="body2" className="text-gray-600 text-center">
                            Need help? Contact{' '}
                            <span className="text-talexo-blue font-medium cursor-pointer hover:underline">
                                info@talexo.co.in
                            </span>
                        </Typography>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default AdminLoginPopup;